import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { BoundedContent, Box } from '@vp/swan';
import { useStyles } from '@vp/ubik-context';
import BoyrunText from '../../../../components/BoyrunText/BoyrunText';
import BoxWithCloudBackground from '../BoxWithCloudBackground/BoxWithCloudBackground';
import styles, { stylesheet } from './LogoToLifeShowcase.module.scss';
import ParallaxItem from './ParallaxItem';

const LogoToLifeShowcase = () => {
    const [scrollOffset, setScrollOffset] = useState(0);
    const sectionRef = useRef<HTMLElement>(null);
    const { t } = useTranslation();
    useStyles(stylesheet);

    useEffect(() => {
        let ticking = false;

        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    if (sectionRef.current) {
                        const rect = sectionRef.current.getBoundingClientRect();
                        const windowHeight = window.innerHeight;

                        // Calculate offset relative to section being centered in viewport
                        // 0 when section is centered, negative when above, positive when below
                        const sectionCenter = rect.top + rect.height / 2;
                        const viewportCenter = windowHeight / 2;
                        const offset = viewportCenter - sectionCenter;

                        setScrollOffset(offset);
                    }
                    ticking = false;
                });
                ticking = true;
            }
        };

        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <BoundedContent as="section" id="logo-to-life" ref={sectionRef} paddingX="0">
            <BoxWithCloudBackground variation="logo-to-life">
                <Box className={styles.logoToLifeShowcaseCanvas}>
                    <ParallaxItem
                        aspectRatio={546 / 577}
                        url={t(
                            'Logomaker GenAi Landing Page > Logo To Life Showcase > Image 1.src',
                        )}
                        scrollOffset={scrollOffset}
                        parallaxSpeed={0.05}
                        id="TShirt"
                        altText={t(
                            'Logomaker GenAi Landing Page > Logo To Life Showcase > Image 1.altText',
                        )}
                    >
                        <BoyrunText>
                            <Trans
                                i18nKey="Logomaker GenAi Landing Page > Logo To Life Showcase > Text 1.value"
                                defaults="Dream it, <br />then print it."
                            />
                        </BoyrunText>
                    </ParallaxItem>
                    <ParallaxItem
                        aspectRatio={464 / 508}
                        url={t(
                            'Logomaker GenAi Landing Page > Logo To Life Showcase > Image 2.src',
                        )}
                        scrollOffset={scrollOffset}
                        parallaxSpeed={0.1}
                        id="Cap"
                        altText={t(
                            'Logomaker GenAi Landing Page > Logo To Life Showcase > Image 2.altText',
                        )}
                    >
                        <BoyrunText>
                            <Trans
                                i18nKey="Logomaker GenAi Landing Page > Logo To Life Showcase > Text 2.value"
                                defaults="Now, that's<br />a cool idea!"
                            />
                        </BoyrunText>
                    </ParallaxItem>
                    <ParallaxItem
                        aspectRatio={300 / 362}
                        url={t(
                            'Logomaker GenAi Landing Page > Logo To Life Showcase > Image 3.src',
                        )}
                        scrollOffset={scrollOffset}
                        parallaxSpeed={0.15}
                        id="DogCollar"
                        altText={t(
                            'Logomaker GenAi Landing Page > Logo To Life Showcase > Image 3.altText',
                        )}
                    />
                    <ParallaxItem
                        aspectRatio={565 / 519}
                        url={t(
                            'Logomaker GenAi Landing Page > Logo To Life Showcase > Image 4.src',
                        )}
                        scrollOffset={scrollOffset}
                        parallaxSpeed={0.08}
                        id="BusinessCards"
                        altText={t(
                            'Logomaker GenAi Landing Page > Logo To Life Showcase > Image 4.altText',
                        )}
                    >
                        <BoyrunText>
                            {t(
                                'Logomaker GenAi Landing Page > Logo To Life Showcase > Text 3.value',
                                `Make it real`,
                            )}
                        </BoyrunText>
                    </ParallaxItem>
                    <ParallaxItem
                        aspectRatio={573 / 627}
                        url={t(
                            'Logomaker GenAi Landing Page > Logo To Life Showcase > Image 5.src',
                        )}
                        scrollOffset={scrollOffset}
                        parallaxSpeed={0.06}
                        id="Font"
                        altText={t(
                            'Logomaker GenAi Landing Page > Logo To Life Showcase > Image 5.altText',
                        )}
                    />
                    <ParallaxItem
                        aspectRatio={645 / 564}
                        url={t(
                            'Logomaker GenAi Landing Page > Logo To Life Showcase > Image 6.src',
                        )}
                        scrollOffset={scrollOffset}
                        parallaxSpeed={0.11}
                        id="Flask"
                        altText={t(
                            'Logomaker GenAi Landing Page > Logo To Life Showcase > Image 6.altText',
                        )}
                    >
                        <BoyrunText>
                            <Trans
                                i18nKey="Logomaker GenAi Landing Page > Logo To Life Showcase > Text 4.value"
                                defaults="Imagine<br />that!"
                            />
                        </BoyrunText>
                    </ParallaxItem>
                    <ParallaxItem
                        aspectRatio={835 / 680}
                        url={t(
                            'Logomaker GenAi Landing Page > Logo To Life Showcase > Image 7.src',
                        )}
                        scrollOffset={scrollOffset}
                        parallaxSpeed={0.1}
                        id="AFrameSign"
                        altText={t(
                            'Logomaker GenAi Landing Page > Logo To Life Showcase > Image 7.altText',
                        )}
                    />
                    <ParallaxItem
                        aspectRatio={1}
                        url={t(
                            'Logomaker GenAi Landing Page > Logo To Life Showcase > Image 8.src',
                        )}
                        scrollOffset={scrollOffset}
                        parallaxSpeed={0.04}
                        id="Ball"
                        altText={t(
                            'Logomaker GenAi Landing Page > Logo To Life Showcase > Image 8.altText',
                        )}
                    />
                </Box>
            </BoxWithCloudBackground>
        </BoundedContent>
    );
};

export default LogoToLifeShowcase;
